import { useMemo } from 'react';

import type { ILead } from 'src/services/api';
import type { IMessage } from 'src/services/api/messages/types';
import { useExtractFacts } from 'src/services/api/leads/use-extract-facts';

import {
    collapseIncome,
    computeLlmRequest,
    computeSimpleSuggestions,
    dedupeByKey,
    factsToSuggestions,
    resurfacePendingSuggestions,
    toSpeakerMessages,
} from 'src/services/lead-facts/suggestions';
import type { IFactSuggestion, SpeakerMessage } from 'src/services/lead-facts/types';

const NO_REQUEST = { shouldCall: false, slice: [] as SpeakerMessage[] };

/**
 * Client-side lead-fact suggestions, recomputed as the conversation changes. Phone/email/
 * pet are detected locally; income/household/voucher trigger one budget-enforced backend
 * call. Returns the merged list; `enabled=false` short-circuits the whole pipeline.
 */
export const useLeadFactSuggestions = (
    lead: ILead,
    messages: IMessage[],
    enabled = true
): IFactSuggestion[] => {
    const speakerMessages = useMemo(() => toSpeakerMessages(messages), [messages]);

    const ruleSuggestions = useMemo(
        () => (enabled ? computeSimpleSuggestions(speakerMessages, lead) : []),
        [speakerMessages, lead, enabled]
    );
    const storedSuggestions = useMemo(
        () => (enabled ? resurfacePendingSuggestions(lead) : []),
        [lead, enabled]
    );
    const request = useMemo(
        () => (enabled ? computeLlmRequest(speakerMessages, lead) : NO_REQUEST),
        [speakerMessages, lead, enabled]
    );

    const { data } = useExtractFacts(lead.uuid, request);
    const llmSuggestions = useMemo(
        () => (enabled ? factsToSuggestions(data?.facts, lead) : []),
        [data, lead, enabled]
    );

    return useMemo(
        () => collapseIncome(dedupeByKey([...ruleSuggestions, ...storedSuggestions, ...llmSuggestions])),
        [ruleSuggestions, storedSuggestions, llmSuggestions]
    );
};
